import { urlWithParams } from '../CommonUtils/urlWithParams';
import fetchWithDefaults from '../CommonUtils/fetchWithDefaults';
import { HttpError } from '../errors/HttpError';

import { receiveData as debtReceiveData } from '../DebtManagementChart/DebtManagementChartActions';
import { receiveData as healthReceiveData } from '../HealthCareChart/HealthCareChartActions';
import { receiveData as retirementReceiveData } from '../RetirementAssetsChart/RetirementAssetsChartActions';
import { receiveData as recommendedReceivedData } from '../RecommendedTargetAmountChart/RecommendedTargetAmountChartActions';
import { receiveData as educationReceiveData } from '../EducationChart/EducationChartActions';

export const RECEIVE_GOAL_DATA = 'GOAL_CAROUSEL.RECEIVE_GOAL_DATA';
export const CHANGE_GOAL_INDEX = 'GOAL_CAROUSEL.CHANGE_GOAL_INDEX';
export const UNSET_INITIALIZE = 'GOAL_CAROUSEL.UNSET_INITIALIZE';

export const CLEAR_DATA = 'GOAL_CAROUSEL.CLEAR_DATA';

const actionsMap = {
  debtManagement: debtReceiveData,
  healthCare: healthReceiveData,
  retirement: retirementReceiveData,
  emergencyFund: recommendedReceivedData,
  education: educationReceiveData,
  largePurchase: recommendedReceivedData,
  homePurchase: recommendedReceivedData,
};

export const receiveGoalData = (
  goalsList,
  initialGoalData,
  initialGoalIndex,
  clientId,
  initialFpId,
) => ({
  type: RECEIVE_GOAL_DATA,
  goalsList,
  initialGoalData,
  initialGoalIndex,
  initialFpId,
  clientId,
});

export const clearCarouselData = () => ({
  type: CLEAR_DATA,
});

export const changeGoalIndex = (index, clientId) => ({
  type: CHANGE_GOAL_INDEX,
  index,
  clientId,
});

export const parseChartName = string => {
  const parsed = string.replace(/_/g, '');
  return `${parsed.charAt(0).toLowerCase()}${parsed.slice(1)}`;
};

export const parseResponse = data => {
  const goalsList = data.GoalList.map((item, index) => ({
    id: item.Id,
    index,
    name: item.Name,
    component: parseChartName(item.ChartName),
  }));

  Error('Default goal chart must exist');

  const initialGoalIndex = goalsList.findIndex(goal => goal.id === data.GoalChart.Id);

  if (initialGoalIndex === -1) {
    throw new Error('Default goal chart must be found in the GoalList');
  }

  const initialGoalData = goalsList[initialGoalIndex];
  initialGoalData.chart = data.GoalChart.Chart;
  return {
    goalsList,
    initialGoalIndex,
    initialGoalData,
  };
};

export const fetchData = (clientId, fpId, goalId = null) => async dispatch => {
  try {
    const params = {
      clientId,
      fpId,
      ...(goalId ? { goalId } : {}),
    };

    const response = await fetchWithDefaults(urlWithParams('/Consumer/GoalChart', params), {
      showSpinner: true,
    });

    if (response.ok) {
      const { payload, success } = await response.json();

      if (success) {
        try {
          const { goalsList, initialGoalData, initialGoalIndex } = parseResponse(payload.viewData);

          const action = actionsMap[initialGoalData.component];

          dispatch(action(clientId, initialGoalData.chart.Data, initialGoalData.id));
          dispatch(receiveGoalData(goalsList, initialGoalData, initialGoalIndex, clientId, fpId));
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(
            'API returned an unexpected response. This could have happened because we have a financial plan but the goal chart is not displayed in the overview widget.',
          );
        }

        return;
      }

      // No error state is defined for this component - error handling needs to be improved
      throw new Error('Response status is not success.');
    }

    throw new HttpError(response);
  } catch (err) {
    console.error(err.toString()); // eslint-disable-line no-console
  }
};

export const fetchDataAndAllCharts = (clientId, fpId) => async dispatch => {
  const response = await fetchWithDefaults(
    urlWithParams('/Consumer/GoalChart', {
      clientId,
      fpId,
    }),
    { showSpinner: true },
  );

  const { success, payload } = await response.json();

  if (!success) {
    return;
  }
  let parsed = null;

  try {
    parsed = parseResponse(payload.viewData);
  } catch (exception) {
    // eslint-disable-next-line no-console
    console.warn(
      'API returned an unexpected response. This could have happened because we have a financial plan but the goal chart is not displayed in the overview widget.',
    );
    return;
  }

  await Promise.all(
    parsed.goalsList.map(async ({ id }, index) => {
      const goalResponse = await fetchWithDefaults(
        urlWithParams('/Consumer/GoalChart', {
          clientId,
          fpId,
          goalId: id,
        }),
        { showSpinner: true },
      );

      const { success: goalSuccess, payload: goalPayload } = await goalResponse.json();

      if (goalSuccess) {
        parsed.goalsList[index].chart = goalPayload.viewData;
      }
    }),
  );

  const action = actionsMap[parsed.initialGoalData.component];

  dispatch(action(clientId, parsed.initialGoalData.chart.Data, parsed.initialGoalData.id));
  dispatch(
    receiveGoalData(
      parsed.goalsList,
      parsed.initialGoalData,
      parsed.initialGoalIndex,
      clientId,
      fpId,
    ),
  );
};
